import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Button Component
 * Apple-inspired button variants with refined states and smooth transitions
 * Follows design tokens from tokens.css
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-base focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-app-focus disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary - confident blue action (canonical name)
        primary:
          "bg-app-primary text-app-primary-text rounded-lg shadow-app-sm hover:bg-app-primary-hover active:bg-app-primary-active active:scale-[0.98]",

        // Default - alias for primary (kept for backward compat)
        default:
          "bg-app-primary text-app-primary-text rounded-lg shadow-app-sm hover:bg-app-primary-hover active:bg-app-primary-active active:scale-[0.98]",

        // Secondary - subtle gray, minimal
        secondary:
          "bg-app-secondary text-app-secondary-text rounded-lg shadow-app-xs hover:bg-app-secondary-hover active:scale-[0.98]",
        
        // Outline - bordered, transparent
        outline:
          "border border-app-border bg-transparent text-app-text rounded-lg hover:bg-app-surface hover:border-app-border-hover active:scale-[0.98]",
        
        // Ghost - minimal hover state only
        ghost: 
          "text-app-text rounded-lg hover:bg-app-secondary active:scale-[0.98]",
        
        // Error - clear red for dangerous actions (HUD v1.0 canonical)
        error:
          "bg-app-error text-white rounded-lg shadow-app-sm hover:opacity-90 active:scale-[0.98]",
        
        // Accent - calm green (canonical name)
        accent:
          "bg-app-success text-white rounded-lg shadow-app-sm hover:opacity-90 active:scale-[0.98]",

        // Success - alias for accent (kept for backward compat)
        success:
          "bg-app-success text-white rounded-lg shadow-app-sm hover:opacity-90 active:scale-[0.98]",
        
        // Link - underlined text
        link: 
          "text-app-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-10 sm:h-8 px-3 text-xs rounded-lg",
        default: "h-12 sm:h-10 px-4 py-3 sm:py-2 text-sm rounded-lg",
        lg: "h-12 sm:h-12 px-6 py-3 sm:py-3 text-base rounded-xl",
        icon: "h-12 w-12 sm:h-10 sm:w-10 rounded-lg",
        "icon-sm": "h-10 w-10 sm:h-8 sm:w-8 rounded-lg",
        "icon-lg": "h-12 w-12 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    (<Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />)
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
